import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { StaffSearchService, SearchProfileRecord, SearchSecondaryFilter } from '../services/staff-search.service';
import { StaffSessionService } from '../services/staff-session.service';

const SEARCH_DEBOUNCE_MS = 500;

@Component({
  selector: 'app-staff-search',
  templateUrl: './staff-search.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-search.component.css']
})
export class StaffSearchComponent implements OnInit {
  staffId: number | string | null = null;

  query = '';
  filter: SearchSecondaryFilter = 'ALL';
  // The full result set for the current keyword — students and staff always
  // come back together in one call (secondaryFilter is always 'ALL'); the
  // All/Student/Staff tabs just filter this list on the client, no re-fetch.
  allResults: SearchProfileRecord[] = [];
  isFetching = false;
  hasSearched = false;

  private searchDebounceTimer: any;

  constructor(
    private staffSearchService: StaffSearchService,
    private staffSession: StaffSessionService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const staffDetails = this.staffSession.getStaffDetails();
    const info = this.staffSession.getSchoolAndStaffInfo();
    this.staffId = staffDetails?.staffId ?? info?.StaffId ?? null;
  }

  get results(): SearchProfileRecord[] {
    return this.filter === 'ALL' ? this.allResults : this.allResults.filter(r => r.userType === this.filter);
  }

  // Debounced auto-search per keystroke — isFetching flips on immediately
  // (before the request fires) so the skeleton list appears the instant
  // typing starts, not only once the network call begins.
  onChangedText(value: string) {
    this.query = value;
    clearTimeout(this.searchDebounceTimer);
    if (!value.trim()) {
      this.allResults = [];
      this.hasSearched = false;
      this.isFetching = false;
      return;
    }
    this.isFetching = true;
    this.searchDebounceTimer = setTimeout(() => this.fetchSearchResults(value), SEARCH_DEBOUNCE_MS);
  }

  doSearch() {
    if (!this.query.trim()) { return; }
    clearTimeout(this.searchDebounceTimer);
    this.isFetching = true;
    this.fetchSearchResults(this.query);
  }

  clearSearch() {
    clearTimeout(this.searchDebounceTimer);
    this.query = '';
    this.allResults = [];
    this.hasSearched = false;
    this.isFetching = false;
  }

  // Switching tabs only re-filters the already-fetched list — no API call.
  setFilterValue(value: SearchSecondaryFilter) {
    this.filter = value;
  }

  private async fetchSearchResults(keyword: string) {
    if (!this.staffId) { this.isFetching = false; return; }
    try {
      const response = await firstValueFrom(this.staffSearchService.searchProfile(keyword, 'ALL', this.staffId));
      this.allResults = response || [];
    } catch {
      this.allResults = [];
    } finally {
      this.hasSearched = true;
      this.isFetching = false;
    }
  }

  // ---------- Display helpers (defensive fallback chains) ----------

  displayName(r: SearchProfileRecord): string {
    return r.userName || r.staffName || r.studentName || '';
  }

  displayImage(r: SearchProfileRecord): string | null {
    return r.userImage || r.staffImage || r.studentImage || null;
  }

  displaySubtitle(r: SearchProfileRecord): string {
    const base = r.classNumber || r.designation || '';
    return r.classDivision ? `${base}-${r.classDivision}` : base;
  }

  openResult(r: SearchProfileRecord) {
    if (r.userType === 'STUDENT') {
      this.router.navigate(['/staff/student', r.userId]);
    } else {
      this.router.navigate(['/staff/staff-member', r.userId]);
    }
  }
}
